import { useState } from 'react'
import {
  Box, Typography, Card, Button, TextField, Stack,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, CircularProgress, Chip, IconButton, Tooltip,
} from '@mui/material'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import { Edit, CurrencyExchange, Add } from '@mui/icons-material'
import { useExchangeRates, useUpsertExchangeRate } from '../../hooks/usePlatformAdmin'
import { adminGridSx } from '../../layouts/AdminLayout'
import { AdminDataError } from '../../components/AdminDataError'
import { AdminPageHeader } from '../../components/AdminPageHeader'
import { AdminEmptyState } from '../../components/AdminEmptyState'

interface RateForm {
  from_currency: string
  to_currency: string
  rate: number | string
}

const emptyForm: RateForm = { from_currency: 'USD', to_currency: '', rate: '' }

export default function AdminExchangeRatesPage() {
  const ratesQuery = useExchangeRates()
  const { data: rates = [], isLoading } = ratesQuery
  const upsert = useUpsertExchangeRate()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [form, setForm] = useState<RateForm>(emptyForm)
  const [editMode, setEditMode] = useState(false)

  const openCreate = () => {
    setForm(emptyForm)
    setEditMode(false)
    setDialogOpen(true)
  }

  const openEdit = (rate: { from_currency: string; to_currency: string; rate: number }) => {
    setForm({ from_currency: rate.from_currency, to_currency: rate.to_currency, rate: rate.rate })
    setEditMode(true)
    setDialogOpen(true)
  }

  const handleSave = async () => {
    const rateValue = typeof form.rate === 'string' ? parseFloat(form.rate) : form.rate
    if (!form.from_currency || !form.to_currency || isNaN(rateValue) || rateValue <= 0) return

    try {
      await upsert.mutateAsync({
        from_currency: form.from_currency.toUpperCase(),
        to_currency: form.to_currency.toUpperCase(),
        rate: rateValue,
      })
      setDialogOpen(false)
    } catch { /* API interceptor shows the failure */ }
  }

  const rateValue = Number(form.rate)
  const canSave = form.from_currency.length === 3 &&
    form.to_currency.length === 3 &&
    form.from_currency !== form.to_currency &&
    Number.isFinite(rateValue) && rateValue > 0 && rateValue <= 1_000_000

  const columns: GridColDef[] = [
    {
      field: 'from_currency',
      headerName: 'From',
      width: 120,
      renderCell: (p) => <Chip label={p.value} size="small" variant="outlined" sx={{ fontWeight: 600 }} />,
    },
    {
      field: 'to_currency',
      headerName: 'To',
      width: 120,
      renderCell: (p) => <Chip label={p.value} size="small" variant="outlined" sx={{ fontWeight: 600 }} />,
    },
    {
      field: 'rate',
      headerName: 'Rate',
      width: 180,
      renderCell: (p) => (
        <Typography variant="body2" fontWeight={700} color="#34A8E0" fontFamily="monospace">
          {Number(p.value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 })}
        </Typography>
      ),
    },
    {
      field: 'conversion',
      headerName: 'Example',
      flex: 1,
      sortable: false,
      renderCell: (p) => (
        <Typography variant="caption" color="#64748b">
          1 {p.row.from_currency} = {Number(p.row.rate).toLocaleString()} {p.row.to_currency}
        </Typography>
      ),
    },
    {
      field: 'updated_at',
      headerName: 'Last Updated',
      width: 180,
      renderCell: (p) => (
        <Typography variant="caption" color="#94a3b8">
          {p.value ? new Date(p.value).toLocaleString() : '—'}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      renderCell: (p) => (
        <Tooltip title="Edit rate">
          <IconButton size="small" onClick={() => openEdit(p.row)}>
            <Edit fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ]

  return (
    <Box>
      <AdminPageHeader
        eyebrow="Finance settings"
        title="Exchange Rates"
        description="Maintain the conversion registry used by billing clients and integrations."
        icon={<CurrencyExchange />}
        actions={<Button variant="contained" startIcon={<Add />} onClick={openCreate}>Add Rate</Button>}
      />

      {ratesQuery.isError && (
        <AdminDataError message="Exchange rates could not be loaded." onRetry={() => { void ratesQuery.refetch() }} />
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#34A8E0' }} />
        </Box>
      ) : rates.length === 0 ? (
        <Card sx={{ borderStyle: 'dashed' }}>
          <AdminEmptyState icon={<CurrencyExchange />} title="No exchange rates configured" description="Add the first conversion rate to enable multi-currency billing." actionLabel="Add rate" onAction={openCreate} />
        </Card>
      ) : (
        <Card sx={{ overflow: 'hidden' }}>
          <DataGrid
            rows={rates}
            columns={columns}
            autoHeight
            pageSizeOptions={[10, 25]}
            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
            sx={adminGridSx}
          />
        </Card>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editMode ? 'Edit Exchange Rate' : 'Add Exchange Rate'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={2}>
              <TextField
                label="From Currency"
                value={form.from_currency}
                onChange={(e) => setForm((p) => ({ ...p, from_currency: e.target.value.toUpperCase() }))}
                fullWidth
                disabled={editMode}
                placeholder="USD"
                inputProps={{ maxLength: 3 }}
                helperText="ISO 4217 code (e.g., USD, EUR, IQD)"
              />
              <TextField
                label="To Currency"
                value={form.to_currency}
                onChange={(e) => setForm((p) => ({ ...p, to_currency: e.target.value.toUpperCase() }))}
                fullWidth
                disabled={editMode}
                placeholder="IQD"
                inputProps={{ maxLength: 3 }}
                helperText="ISO 4217 code"
              />
            </Stack>
            <TextField
              label="Exchange Rate"
              type="number"
              value={form.rate}
              onChange={(e) => setForm((p) => ({ ...p, rate: e.target.value }))}
              fullWidth
              placeholder="1480.00"
              inputProps={{ step: 'any', min: 0 }}
              error={form.rate !== '' && (!Number.isFinite(rateValue) || rateValue <= 0 || rateValue > 1_000_000)}
              helperText={
                form.from_currency && form.to_currency && form.rate
                  ? `1 ${form.from_currency} = ${Number(form.rate).toLocaleString()} ${form.to_currency}`
                  : 'How much of the target currency per 1 unit of source currency'
              }
            />

            {upsert.isError && (
              <Alert severity="error">Failed to save exchange rate. Please try again.</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={upsert.isPending || !canSave}
            sx={{ background: 'linear-gradient(135deg,#34A8E0,#2B8BB9)' }}
          >
            {upsert.isPending ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
